import Loader from "@components/global/Loader/Loader";
import MinerName from "@components/global/new/MinerName/MinerName";
import TooltipText from "@components/global/new/TooltipText/TooltipText";
import { ItemStore } from "@mobx/store/locations/interfaces";
import { IColoredUser3, IRackItem3 } from "@mobx/store/locations/types";
import { useToasts } from "@utils/hooks/useToasts";
import { makeNotifs } from "@utils/makeNotifs";
import { Button } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import styles from "./Menu.module.scss";

export interface IMenu3Props {
  readonly itemStore: ItemStore;
  readonly minerName?: string;
  readonly userId?: number;
  readonly minerId?: number;

  onCloseTooltip(): void;
}

const useControl = (props: IMenu3Props) => {
  const { errors } = makeNotifs();
  const { error: onError } = useToasts();
  const [currentUser, setCurrentUser] = useState<IColoredUser3>();
  const [loadUser, _] = useState(false);
  const rackItem = props.itemStore.item as IRackItem3;
  const minerStore = props.itemStore.createMinerStore();
  const isMiner = !(!!props.userId || !!props.minerId || !!props.minerName);

  useEffect(() => {
    (async () => {
      const res = await props.itemStore.getUser();
      if (!res) return;
      setCurrentUser(res);
    })();
  }, []);

  const onOpenMinerInfo = async () => {
    if (!minerStore) return;
    return minerStore.openMinerInfo();
  };

  const onDelete = async () => {
    if (rackItem.merge && rackItem.merge.length > 0) {
      onError(errors.locations__unnalocated_unmerge);
      return;
    };

    if (!minerStore) return;

    await minerStore.deleteMiner(props.itemStore.itemIndex);
    props.itemStore.turnToSection();
  };

  const canDelete = useMemo(
    () => (rackItem.merge && rackItem.merge.length > 0) || true,
    [rackItem.merge]
  );

  useEffect(() => {
    const onClickOver = () => props.onCloseTooltip();
    document.addEventListener("click", onClickOver);
    return () => document.removeEventListener("click", onClickOver);
  }, []);

  return {
    currentUser,
    loadUser,
    canDelete,
    minerName: props.minerName || rackItem.miner_name,
    isMiner,

    onOpenMinerInfo,
    onDelete,
  };
};

const Menu3 = observer((props: IMenu3Props) => {
  const { formatMessage } = useIntl();
  const control = useControl(props);

  return (
    <div className={styles.wrapper}>
      <TooltipText
        open={
          control.currentUser && control.currentUser.email.length > 9
            ? undefined
            : false
        }
        text={control.currentUser?.email || ""}
      >
        <div
          className={styles.head}
          style={{ backgroundColor: control.currentUser?.color }}
        >
          {control.loadUser && <Loader size={14} />}
          {control.currentUser?.email}
        </div>
      </TooltipText>
      <MinerName className={styles.miner_name}>{control.minerName}</MinerName>
      <div className={styles.btns}>
        <Button
          className={styles.btn}
          size='small'
          disabled={props.itemStore.isLoading}
          onClick={control.onOpenMinerInfo}
        >
          {formatMessage({ id: "location.rack.rack_item.info" })}
        </Button>
        {control.isMiner && (
          <Button
            className={styles.btn}
            size='small'
            disabled={!control.canDelete}
            onClick={control.onDelete}
          >
            {formatMessage({ id: "location.rack.rack_item.delete" })}
          </Button>
        )}
      </div>
    </div>
  );
});

Menu3.displayName = "Menu3";

export default Menu3;
